import { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import HeroMedia from "../components/home/HeroMedia";
import { CTAButton } from "../components/ui-regera/CTAButton";
import { Overline } from "../components/ui-regera/Section";
import { Frame, Head } from "../components/spec/Frame";
import EngagementCard from "../components/spec/EngagementCard";
import { useProjects, useServices } from "../lib/hooks";
import { HOME, META_PAGE } from "../data/content";
import { numberedServices } from "../data/catalog";

export default function Home() {
  const { data: projects = [] } = useProjects();
  const services = useServices();
  const serviceList = useMemo(() => numberedServices(services), [services]);
  useEffect(() => {
    document.title = "REGERA — Technology that moves business forward";
  }, []);
  const o = HOME.opening;

  return (
    <div data-testid="home-page">
      {/* 1 · Opening */}
      <Frame id="home-opening" testId="hero" first>
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6">
            <Overline>{o.overline}</Overline>
            <h1 className="mt-6 font-display font-black text-white tracking-tight leading-[1.02] text-4xl sm:text-5xl xl:text-6xl rg-fs-display">
              {o.title}
            </h1>
            <p className="mt-6 text-white/80 text-base lg:text-lg leading-relaxed max-w-xl rg-fs-lead">{o.body}</p>
            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              <CTAButton to="/services" variant="ghost" testId="hero-capabilities-cta">Explore Our Capabilities</CTAButton>
              <CTAButton to="/contact#enquiry" testId="hero-engage-cta">Engage REGERA</CTAButton>
            </div>
          </div>
          <div className="lg:col-span-6 relative h-[260px] sm:h-[340px] lg:h-[min(60vh,620px)] border border-white/10 bg-[#0b0b0b] overflow-hidden" aria-hidden="true">
            <HeroMedia />
          </div>
        </div>
      </Frame>

      {/* 2 · What REGERA does */}
      <Frame id="home-does" testId="home-does">
        <Head {...HOME.does} fit />
        <div className="rg-gap mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
          {HOME.does.pillars.map((p, i) => (
            <div key={p.title} data-pg-item className="bg-[#0a0a0a] p-6 lg:p-8 rg-pad">
              <span className="font-display text-sm font-bold text-[#e60000]">{String(i + 1).padStart(2, "0")}</span>
              <h3 className="mt-4 font-display text-xl font-bold text-white rg-fs-h3">{p.title}</h3>
              <p className="mt-3 text-white/70 leading-relaxed rg-fs-text">{p.text}</p>
            </div>
          ))}
        </div>
      </Frame>

      {/* 3 · Services */}
      <Frame id="home-services" testId="home-services">
        <Head {...HOME.services} fit />
        <div className="rg-gap mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
          {serviceList.map((s) => (
            <Link
              key={s.slug}
              to={`/services#${s.slug}`}
              data-pg-item
              data-testid={`capability-${s.slug}`}
              className="group bg-[#0a0a0a] hover:bg-[#141414] p-5 lg:p-6 rg-pad flex items-start gap-4 transition-colors"
            >
              <span className="font-display text-sm font-bold text-[#e60000] mt-1">{s.n}</span>
              <span className="flex-1 font-display text-lg font-bold text-white leading-snug group-hover:text-[#e60000] transition-colors rg-fs-item">{s.title}</span>
              <ArrowUpRight className="h-4 w-4 text-white/40 group-hover:text-[#e60000] mt-1.5 shrink-0" />
            </Link>
          ))}
        </div>
      </Frame>

      {/* 4 · Metamorphosis */}
      <Frame id="home-metamorphosis" testId="home-metamorphosis">
        <Head {...HOME.meta} fit />
        <ol className="rg-gap mt-10 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-px bg-white/10 border border-white/10">
          {META_PAGE.steps.map((s) => (
            <li key={s.n} className="bg-[#0a0a0a] p-4 lg:p-5 rg-pad">
              <span className="font-display text-sm font-bold text-[#e60000]">{s.n}</span>
              <p className="mt-2 font-display font-bold text-white rg-fs-h3">{s.title}</p>
              {/* step descriptions from the Metamorphosis page give this frame its substance */}
              <p className="mt-2 text-white/60 text-sm leading-relaxed rg-fs-small">{s.text}</p>
            </li>
          ))}
        </ol>
        <CTAButton to="/metamorphosis" variant="ghost" testId="home-meta-cta" className="rg-gap mt-10">Explore Metamorphosis</CTAButton>
      </Frame>

      {/* 5 · Experience & engagement */}
      <Frame id="home-experience" testId="portfolio-preview">
        <div className="grid lg:grid-cols-12 gap-8 lg:items-end">
          <Head className="lg:col-span-6" overline={HOME.experience.overline} title={HOME.experience.title} fit />
          <div className="lg:col-span-6">
            <p className="text-white/80 text-base lg:text-lg leading-relaxed rg-fs-lead">{HOME.experience.body}</p>
            <div className="mt-6 flex flex-wrap items-center gap-6">
              <CTAButton to="/contact#enquiry" testId="home-engage-cta">Engage REGERA</CTAButton>
              <Link to="/portfolio" data-testid="portfolio-view-all" className="text-sm tracking-[0.14em] uppercase text-white/80 hover:text-[#e60000] inline-flex items-center gap-2">
                Transformation <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
        <div className="mt-10 grid md:grid-cols-3 gap-5">
          {projects.slice(0, 3).map((p) => <EngagementCard key={p.slug} p={p} />)}
        </div>
      </Frame>
    </div>
  );
}
